import { PlayingLevel, PlayingRangeBadge } from '../components/PlayingLevel'
import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { formatFacilityBookingStatus } from '../utils/facilityBookingPresentation'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Clock, MapPin, Sun } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import {
  fetchMemberRacquetCourts,
  formatSportTypeLabel,
  MEMBER_RACQUET_SPORTS,
} from '../api/racquetCourts'
import { formatCurrencyAmount } from '@/utils/currency'
import MemberMobileBookingHeader from '../components/MemberMobileBookingHeader'
import { deriveMemberPortalRacquetDurationOptions } from '../utils/racquetDurationOptions'

function startOfDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function formatDateLabel(date, locale) {
  return date.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })
}

function formatCompactDateLabel(date, locale) {
  return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })
}

function addDays(date, delta) {
  const d = new Date(date)
  d.setDate(d.getDate() + delta)
  return d
}

function isSameDay(a, b) {
  if (!(a instanceof Date) || Number.isNaN(a.getTime())) return false
  if (!(b instanceof Date) || Number.isNaN(b.getTime())) return false
  return a.toDateString() === b.toDateString()
}

function formatDateParam(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseDateParam(value) {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
  if (match) {
    const [, yStr, mStr, dStr] = match
    const parsed = new Date(Number.parseInt(yStr, 10), Number.parseInt(mStr, 10) - 1, Number.parseInt(dStr, 10))
    if (!Number.isNaN(parsed.getTime())) return startOfDay(parsed)
  }
  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : startOfDay(fallback)
}

function formatDayTileLabel(date, locale) {
  return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' })
}

function formatMonthLabel(date, locale) {
  return date.toLocaleDateString(locale, { month: 'short' })
}

const DAYS_VISIBLE = 10

function parseTimeToMinutes(value) {
  if (!value || typeof value !== 'string') return 0
  const [h, m] = value.split(':').map((v) => parseInt(v, 10))
  const hours = Number.isFinite(h) ? h : 0
  const minutes = Number.isFinite(m) ? m : 0
  return hours * 60 + minutes
}

function minutesToDate(date, minutes) {
  const base = startOfDay(date)
  return new Date(base.getTime() + minutes * 60 * 1000)
}

function formatMinutesToTime(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function formatTime(minutes, date, locale) {
  return minutesToDate(date, minutes).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  const initials = [parts[0]?.[0], parts[1]?.[0]].filter(Boolean).join('')
  return (initials || 'P').toUpperCase()
}

function formatMatchDateTime(value, locale, memberText, timeZone) {
  const asDate = new Date(value)
  if (Number.isNaN(asDate.getTime())) return memberText("membersStaff.smsDelivery.scheduled")
  return asDate.toLocaleString(locale, { ...(timeZone ? { timeZone } : {}), weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function mapParticipantStatusLabel(status, t) {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'pending') return t('portalPresentation.member_awaiting_approval')
  return formatFacilityBookingStatus(normalized === 'confirmed' ? 'paid' : normalized === 'pending_confirmation' ? 'pending_payment' : normalized, t)
}

function mapOpenMatchParticipantPublicLabel({ isHost }, memberText) {
  if (isHost) return memberText("portalPresentation.member_host")
  return memberText("portalPresentation.joined")
}

function buildSlots(court, date) {
  if (!court) return []
  const interval = Number(court.default_duration_minutes) || 60
  const startMinutes = parseTimeToMinutes(court.bookable_start_time)
  const endMinutes = parseTimeToMinutes(court.bookable_end_time)
  const now = new Date()

  const slots = []
  for (let current = startMinutes; current + interval <= endMinutes; current += interval) {
    const slotStart = minutesToDate(date, current)
    if (slotStart.getTime() <= now.getTime()) continue
    slots.push({
      startMinutes: current,
      endMinutes: Math.min(current + interval, endMinutes),
      label: `${formatTime(current, date)} – ${formatTime(Math.min(current + interval, endMinutes), date)}`,
    })
  }

  return slots
}

function isSlotBlocked(slot, selectedDate, bookingRanges) {
  const slotStartMs = minutesToDate(selectedDate, slot.startMinutes).getTime()
  const slotEndMs = minutesToDate(selectedDate, slot.endMinutes).getTime()
  return bookingRanges.some(([startMs, endMs]) => slotStartMs < endMs && slotEndMs > startMs)
}

function CourtCard({ court, currencyCode, isSelected, onSelect }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const hasMemberRate = Number.isFinite(Number(court?.member_hourly_rate))
  const hasGuestRate = Number.isFinite(Number(court?.guest_hourly_rate))

  return (
    <button
      type="button"
      className={`mp-racquet-card ${isSelected ? 'is-selected' : ''}`}
      onClick={() => onSelect?.(court)}
    >
      <div className="mp-racquet-card__header">
        <div>
          <p className="mp-eyebrow">{memberText("racquetPlay.court")}</p>
          <div className="mp-racquet-card__name">{court.name}</div>
        </div>
        <div className="mp-chip subtle">{court.is_outdoor ? memberText("racquetPlay.outdoor") : memberText("racquetPlay.indoor")}</div>
      </div>
      <div className="mp-racquet-card__meta">
        <div className="mp-racquet-card__line">
          <Clock size={16} />
          <span>
            {court.bookable_start_time} – {court.bookable_end_time}
          </span>
        </div>
        <div className="mp-racquet-card__line">
          <Sun size={16} />
          <span>{memberText("portalPresentation.member_default_session")} {court.default_duration_minutes} {memberText("portalPresentation.member_min")}</span>
        </div>
        <div className="mp-racquet-card__rates">
          {hasMemberRate ? (
            <span>{memberText("portalPresentation.member_member")} {formatCurrencyAmount(Number(court.member_hourly_rate), currencyCode, { locale })}</span>
          ) : (
            <span>{memberText("portalPresentation.member_member_rate_on_file")}</span>
          )}
          {hasGuestRate ? (
            <span>{memberText("portalPresentation.member_guest")} {formatCurrencyAmount(Number(court.guest_hourly_rate), currencyCode, { locale })}</span>
          ) : (
            <span>{memberText("portalPresentation.member_guest_rate_on_file")}</span>
          )}
        </div>
      </div>
    </button>
  )
}

function MobileCourtChoice({ court, currencyCode, slot, selectedDate, bookingRanges, selectedChoice, onSelect }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const dayEnd = parseTimeToMinutes(court.bookable_end_time)
  const nextBookingStart = bookingRanges
    .map(([start]) => Math.round((start - startOfDay(selectedDate).getTime()) / 60000))
    .filter((start) => start > slot.startMinutes)
    .sort((a, b) => a - b)[0]
  const maxAllowedMinutes = Math.max(0, Math.min(dayEnd, nextBookingStart || dayEnd) - slot.startMinutes)
  const durations = deriveMemberPortalRacquetDurationOptions({ court, maxAllowedMinutes })
  const priceFor = (minutes) => {
    const fixed = Number(court?.duration_price_map?.[String(minutes)])
    if (Number.isFinite(fixed)) return fixed
    const hourly = Number(court?.member_hourly_rate ?? court?.hourly_rate)
    return Number.isFinite(hourly) ? hourly * minutes / 60 : null
  }

  return (
    <article className="mp-racquet-mobile-court">
      <div className="mp-racquet-mobile-court__heading">
        <div><strong>{court.name}</strong><small>{[court.is_outdoor ? memberText("racquetPlay.outdoor") : memberText("racquetPlay.indoor"), court.surface].filter(Boolean).join(' · ')}</small></div>
        <span className="mp-chip subtle">{court.court_size === 'double' ? memberText("racquetPlay.doubles") : court.court_size === 'single' ? memberText("racquetPlay.singles") : formatSportTypeLabel(court.sport_type, memberText)}</span>
      </div>
      <div className="mp-racquet-duration-grid" role="group" aria-label={memberText("portalPresentation.member_choose_duration_for_name", { name: court.name })}>
        {durations.map((minutes) => {
          const projectedPrice = priceFor(minutes)
          const isSelected = String(selectedChoice?.court?.id) === String(court.id) && Number(selectedChoice?.duration) === Number(minutes) && Number(selectedChoice?.slot?.startMinutes) === Number(slot.startMinutes)
          return (
            <button key={minutes} type="button" className={isSelected ? 'is-selected' : ''} aria-pressed={isSelected} onClick={() => onSelect({ slot, court, duration: minutes, projectedPrice })} aria-label={memberText("portalPresentation.member__minutes_minutes_price", { minutes, price: projectedPrice != null ? ', ' + formatCurrencyAmount(projectedPrice, currencyCode, { locale }) : '' })}>
              <strong>{minutes} {memberText("portalPresentation.member_min")}</strong>
              <span>{projectedPrice != null ? formatCurrencyAmount(projectedPrice, currencyCode, { locale }) : memberText("portalPresentation.member_live_price")}</span>
            </button>
          )
        })}
      </div>
    </article>
  )
}

function OpenMatchHostManagementContent({
  managedMatchData,
  managedMatchPlayers,
  managedMatchPendingRequests,
  managedMatchPendingInvites,
  managedMatchActionKey,
  onManageRequestAction,
  isMobile = false,
}) {
  const clubTimeZone = useMemberTimeZone()
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const managedMatchBookingId = managedMatchData?.booking_id || managedMatchData?.id
  const hasPendingInvites = managedMatchPendingInvites.length > 0
  const maxPlayers = Math.max(1, Number(managedMatchData?.max_players || 0) || 4)
  const participants = Array.isArray(managedMatchPlayers) ? managedMatchPlayers : []
  const confirmedCount = Number(managedMatchData?.confirmed_count || participants.length || 0)
  const spotsLeft = Math.max(0, maxPlayers - confirmedCount)
  const joinPolicyLabel = String(managedMatchData?.join_policy || '').toLowerCase() === 'direct_join' ? memberText('portalPresentation.member_direct_join') : memberText('portalPresentation.member_host_approval')
  const overviewCard = (
    <article className="mp-open-match-card mp-open-match-card--manage">
      <div className="mp-open-match-card__header">
        <strong>{new Date(managedMatchData.start_at).toLocaleTimeString(locale, { timeZone: clubTimeZone, hour: 'numeric', minute: '2-digit' })}{isMobile ? ` · ${managedMatchData.court_name || memberText("racquetPlay.court")}` : ''}</strong>
      </div>
      <div className="mp-open-match-card__summary mp-open-match-card__summary--stacked">
        {isMobile ? (
          <span>{confirmedCount}/{maxPlayers} {memberText("portalPresentation.member_players")} {spotsLeft} {spotsLeft === 1 ? 'place' : 'places'} {memberText("portalPresentation.member_open")}</span>
        ) : (
          <><span>{confirmedCount}/{maxPlayers} {memberText("portalPresentation.member_players_2")}</span><span className="mp-chip subtle">{managedMatchData.court_name || memberText("racquetPlay.court")}</span><span>{spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} {memberText("portalPresentation.member_left")}</span></>
        )}
      </div>
      <div className="mp-open-match-card__chips">
        <span className="mp-chip subtle">{memberText(managedMatchData.match_type === 'competitive' ? 'portalPresentation.member_competitive' : 'portalPresentation.member_friendly')}</span>
        <span className="mp-chip subtle">{joinPolicyLabel}</span>
        <PlayingRangeBadge min={managedMatchData.skill_min} max={managedMatchData.skill_max} sport={managedMatchData.sport_type} />
      </div>
      <div className="mp-open-match-card__slots">
        {Array.from({ length: maxPlayers }).map((_, slotIndex) => {
          const player = participants[slotIndex] || null
          const playerName = player?.player_name || player?.member_display_name || memberText("portalPresentation.member_player_caption")
          const isHostPlayer = player && String(player.member_id || '') === String(managedMatchData?.host_member_id || '')
          return (
            <div key={`${managedMatchBookingId || managedMatchData?.id}-slot-${slotIndex}`} className={`mp-open-match-slot ${player ? '' : 'is-empty'}`}>
              {player ? (
                <>
                  {player.avatar_url ? (
                    <img src={player.avatar_url} alt={playerName} className="mp-open-match-slot__avatar" />
                  ) : (
                    <div className="mp-open-match-slot__avatar mp-open-match-slot__avatar--fallback">{getInitials(playerName)}</div>
                  )}
                  <div className="mp-open-match-slot__meta">
                    <span>{playerName}</span>
                    <small>{isHostPlayer ? memberText("portalPresentation.member_host") : mapParticipantStatusLabel(player.status, memberText)}</small><PlayingLevel value={player.playing_level} sport={managedMatchData.sport_type} />
                  </div>
                </>
              ) : (
                <div className="mp-open-match-slot__empty">{memberText("portalPresentation.member_vacant_spot")}</div>
              )}
            </div>
          )
        })}
      </div>
    </article>
  )

  const requestsPanel = (
    <section className={`mp-open-match-manage__requests${isMobile && !managedMatchPendingRequests.length ? ' is-empty' : ''}`}>
      <h6>{isMobile && !managedMatchPendingRequests.length ? memberText("portalPresentation.member_pending_requests") : memberText("portalPresentation.member_pending_join_requests")}</h6>
      {!managedMatchPendingRequests.length ? (
        isMobile ? <span className="mp-open-match-manage__requests-none">{memberText("portalPresentation.member_none")}</span> : <div className="mp-empty subtle">{memberText("portalPresentation.member_no_pending_join_requests_right_now")}</div>
      ) : (
        <div className="mp-open-match-manage__request-list">
          {managedMatchPendingRequests.map((request) => {
            const approveKey = `${managedMatchBookingId}:${request.id}:approve`
            const rejectKey = `${managedMatchBookingId}:${request.id}:reject`
            const isBusy = managedMatchActionKey === approveKey || managedMatchActionKey === rejectKey
            return (
              <article key={request.id} className="mp-open-match-manage__request-card">
                <div className="mp-open-match-manage__request-head">
                  {request.requester_avatar_url ? (
                    <img src={request.requester_avatar_url} alt={request.requester_display_name || memberText("portalPresentation.member_member")} className="mp-open-match-manage__avatar" />
                  ) : (
                    <div className="mp-open-match-manage__avatar mp-open-match-manage__avatar--fallback">
                      {getInitials(request.requester_display_name || memberText("portalPresentation.member_member"))}
                    </div>
                  )}
                  <div>
                    <strong>{request.requester_display_name || memberText("portalPresentation.member_member")}</strong>
                    <div className="mp-text-muted">{formatMatchDateTime(request.created_at, locale, memberText, clubTimeZone)}</div>
                  </div>
                </div>
                {request.message ? <p className="mp-open-match-manage__request-message">{request.message}</p> : null}
                <div className="mp-open-match-manage__request-actions">
                  <button type="button" className="mp-btn mp-btn--ghost" disabled={isBusy} onClick={() => onManageRequestAction({ bookingId: managedMatchBookingId, requestId: request.id, action: 'reject' })}>{memberText("portalPresentation.member_reject")}</button>
                  <button type="button" className="mp-btn mp-btn--primary" disabled={isBusy} onClick={() => onManageRequestAction({ bookingId: managedMatchBookingId, requestId: request.id, action: 'approve' })}>{memberText("portalPresentation.member_approve")}</button>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )

  return (
    <div className={`mp-open-match-manage ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
      <div className="mp-open-match-manage__layout">
        <section className="mp-open-match-manage__overview">
          {overviewCard}
        </section>
        <section className="mp-open-match-manage__actions">
          {requestsPanel}
          {hasPendingInvites ? (
            <section className="mp-open-match-manage__invites">
              <h6>{memberText("portalPresentation.member_pending_invites")}</h6>
              <div className="mp-open-match-manage__invite-list">
                {managedMatchPendingInvites.map((invite) => (
                  <div key={invite.id} className="mp-open-match-manage__invite-row">
                    <span>{invite.requester_display_name || memberText("portalPresentation.member_member")}</span>
                    <small>{formatMatchDateTime(invite.created_at, locale, memberText, clubTimeZone)}</small>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </section>
      </div>
    </div>
  )
}

export default function MemberRacquetBookingPage({ clubCode, isMobileApp = false }) {
  const clubTimeZone = useMemberTimeZone()
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { sportType: sportTypeParam, bookingId: managedBookingIdParam } = useParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()

  const sportType = useMemo(() => String(sportTypeParam || '').toLowerCase(), [sportTypeParam])
  const sportLabel = useMemo(() => formatSportTypeLabel(sportType, memberText), [sportType, memberText])
  const isValidSport = MEMBER_RACQUET_SPORTS.includes(sportType)

  const [courts, setCourts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currencyCode, setCurrencyCode] = useState('GBP')
  const [showAllMobileOpenMatches, setShowAllMobileOpenMatches] = useState(false)
  const [selectedCourtId, setSelectedCourtId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(() => parseDateParam(searchParams.get('date')) || startOfDay(new Date()))
  const [baseDate] = useState(() => startOfDay(new Date()))
  const [reloadKey, setReloadKey] = useState(0)
  const [bookingsByCourtId, setBookingsByCourtId] = useState({})
  const [selectedMobileTimeKey, setSelectedMobileTimeKey] = useState(null)
  const [selectedMobileChoice, setSelectedMobileChoice] = useState(null)
  const [facilityImage, setFacilityImage] = useState(null)
  const [openMatches, setOpenMatches] = useState([])
  const [openMatchesLoading, setOpenMatchesLoading] = useState(false)
  const [openMatchActionNotice, setOpenMatchActionNotice] = useState(null)
  const [openMatchViewers, setOpenMatchViewers] = useState({})
  const [managedMatchId, setManagedMatchId] = useState(null)
  const [managedMatchLoading, setManagedMatchLoading] = useState(false)
  const [managedMatchError, setManagedMatchError] = useState(null)
  const [managedMatchData, setManagedMatchData] = useState(null)
  const [managedMatchNotice, setManagedMatchNotice] = useState(null)
  const [managedMatchActionKey, setManagedMatchActionKey] = useState(null)
  const [payOnlineModalMatch, setPayOnlineModalMatch] = useState(null)
  const mobileCourtSectionRef = useRef(null)
  const dateParam = searchParams.get('date')
  const isManageRoute = Boolean(managedBookingIdParam)

  const availableDates = useMemo(() => {
    return Array.from({ length: DAYS_VISIBLE }, (_, index) => addDays(baseDate, index))
  }, [baseDate])

  useEffect(() => {
    let cancelled = false

    async function loadCourts() {
      if (!clubCode || !isValidSport) {
        setCourts([])
        setCurrencyCode('GBP')
        setLoading(false)
        setError("portalPresentation.member_invalid_sport_type")
        return
      }

      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await fetchMemberRacquetCourts(clubCode, sportType, {
        memberPortalApiGet,
      })

      if (cancelled) return

      if (fetchError) {
        setError("portalPresentation.member_unable_to_load_court_details_right_now")
        setCourts([])
        setCurrencyCode('GBP')
      } else {
        const resolvedCourts = Array.isArray(data?.courts) ? data.courts : Array.isArray(data) ? data : []
        setCourts(resolvedCourts)
        setCurrencyCode(data?.currencyCode || 'GBP')
        setSelectedCourtId((current) => current || (resolvedCourts && resolvedCourts[0]?.id) || null)
      }

      setLoading(false)
    }

    loadCourts()

    return () => {
      cancelled = true
    }
  }, [clubCode, isValidSport, memberPortalApiGet, reloadKey, sportType])

  useEffect(() => {
    let cancelled = false

    async function loadFacilityImage() {
      if (facilityImage || !clubCode || !isValidSport) return
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/facilities`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'facilities_fetch_failed')
        const facilities = Array.isArray(json?.data) ? json.data : []
        const match = facilities.find((facility) => facility?.facility_key === sportType)
        if (!cancelled && match?.image_url) {
          setFacilityImage(match.image_url)
        }
      } catch (err) {
        if (!cancelled) console.warn('Failed to load facility image', err)
      }
    }

    loadFacilityImage()

    return () => {
      cancelled = true
    }
  }, [clubCode, facilityImage, isValidSport, memberPortalApiGet, sportType])

  const selectedCourt = useMemo(
    () => courts.find((court) => String(court.id) === String(selectedCourtId)) || courts[0] || null,
    [courts, selectedCourtId]
  )

  const slots = useMemo(() => buildSlots(selectedCourt, selectedDate), [selectedCourt, selectedDate])

  useEffect(() => {
    let cancelled = false

    async function loadBookings() {
      if (!clubCode || !selectedDate || !Array.isArray(courts) || courts.length === 0) {
        setBookingsByCourtId({})
        return
      }

      const dateKey = formatDateParam(selectedDate)
      if (!dateKey) {
        setBookingsByCourtId({})
        return
      }

      try {
        const responses = await Promise.all(
          courts.map(async (court) => {
            const courtId = String(court.id)
            const res = await memberPortalApiGet(
              `/api/members-portal/${clubCode}/racquet/bookings?date=${dateKey}&court_id=${encodeURIComponent(courtId)}`
            )
            const payload = await res.json().catch(() => null)
            if (!res.ok || !payload?.ok) {
              return [courtId, []]
            }
            return [courtId, Array.isArray(payload?.data) ? payload.data : []]
          })
        )

        if (cancelled) return

        setBookingsByCourtId(Object.fromEntries(responses))
      } catch (err) {
        if (!cancelled) {
          setBookingsByCourtId({})
        }
      }
    }

    loadBookings()

    return () => {
      cancelled = true
    }
  }, [clubCode, courts, memberPortalApiGet, selectedDate])

  const loadOpenMatches = useCallback(async () => {
    if (!clubCode || !selectedDate || !isValidSport) {
      setOpenMatches([])
      setOpenMatchViewers({})
      return false
    }
    const dateKey = formatDateParam(selectedDate)
    if (!dateKey) return false

    setOpenMatchesLoading(true)
    try {
      const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/racquet/open-matches?date=${dateKey}&sport_type=${encodeURIComponent(sportType)}`)
      const payload = await res.json().catch(() => null)
      if (!res.ok || !payload?.ok) {
        setOpenMatches([])
        setOpenMatchViewers({})
        return false
      }
      const nextMatches = Array.isArray(payload?.data) ? payload.data : []
      setOpenMatches(nextMatches)
      setOpenMatchViewers(
        Object.fromEntries(
          nextMatches.map((match) => [String(match?.id || ''), { isHost: match?.is_host === true }])
        )
      )
      return true
    } catch (_error) {
      setOpenMatches([])
      setOpenMatchViewers({})
      return false
    } finally {
      setOpenMatchesLoading(false)
    }
  }, [clubCode, isValidSport, memberPortalApiGet, selectedDate, sportType])

  useEffect(() => {
    let cancelled = false
    async function run() {
      await loadOpenMatches()
      if (cancelled) return
    }
    run()
    return () => {
      cancelled = true
    }
  }, [loadOpenMatches])

  const handleJoinOpenMatch = async (bookingId) => {
    if (!bookingId) return
    try {
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/racquet/open-matches/${bookingId}/request-join`, {})
      const payload = await res.json().catch(() => null)
      if (!res.ok || !payload?.ok) {
        setError("portalPresentation.member_unable_to_join_open_match_right_now")
        return
      }
      const mode = String(payload?.data?.mode || '').toLowerCase()
      if (mode === 'direct_join') {
        setOpenMatchActionNotice("portalPresentation.member_spot_joined_payment_required_to_finalize_your_spot")
      } else if (mode === 'request') {
        setOpenMatchActionNotice("portalPresentation.member_request_sent_you_will_be_notified_if_approved")
      }
      setError(null)
      setReloadKey((key) => key + 1)
    } catch (err) {
      setError("portalPresentation.member_unable_to_join_open_match_right_now")
    }
  }

  const handleOpenMatchPayment = async ({ bookingId, playerId, method }) => {
    if (!bookingId || !playerId || !method) return
    if (method !== 'charge_account') {
      setOpenMatchActionNotice("portalPresentation.member_online_card_payments_are_not_enabled_yet")
      return
    }
    try {
      const res = await memberPortalApiPost(
        `/api/members-portal/${clubCode}/racquet/bookings/${bookingId}/players/${playerId}/confirm`,
        { method }
      )
      const payload = await res.json().catch(() => null)
      if (!res.ok || !payload?.ok) {
        setOpenMatchActionNotice("portalPresentation.member_unable_to_complete_payment_right_now")
        return
      }
      setOpenMatchActionNotice("portalPresentation.member_charged_to_account")
      setError(null)
      await loadOpenMatches()
      if (managedMatchId && String(managedMatchId) === String(bookingId)) {
        await loadManagedMatchDetail()
      }
    } catch (err) {
      setOpenMatchActionNotice("portalPresentation.member_unable_to_complete_payment_right_now")
    }
  }

  const handleOpenPayOnlineModal = ({ matchId, playerId, amountDue, courtName, startAt, viewerName }) => {
    if (!matchId || !playerId) return
    setPayOnlineModalMatch({
      bookingId: matchId,
      playerId,
      amountDue: Number(amountDue || 0),
      courtName: courtName || null,
      startAt: startAt || null,
      viewerName: viewerName || null,
    })
  }

  const handleOpenManageMatch = (bookingId) => {
    if (isMobileApp) {
      const nextSearch = dateParam ? `?date=${encodeURIComponent(dateParam)}` : ''
      navigate(`/${clubCode}/book-racquet/${sportType}/open-match/${bookingId}/manage${nextSearch}`)
      return
    }
    setManagedMatchId(String(bookingId || ''))
    setManagedMatchData(null)
    setManagedMatchError(null)
    setManagedMatchNotice(null)
    setManagedMatchActionKey(null)
  }

  const handleCloseManageMatch = () => {
    if (isManageRoute) {
      const nextSearch = dateParam ? `?date=${encodeURIComponent(dateParam)}` : ''
      navigate(`/${clubCode}/book-racquet/${sportType}${nextSearch}`)
      return
    }
    setManagedMatchId(null)
    setManagedMatchData(null)
    setManagedMatchError(null)
    setManagedMatchNotice(null)
    setManagedMatchActionKey(null)
  }

  useEffect(() => {
    if (!isManageRoute) return
    setManagedMatchId(String(managedBookingIdParam || ''))
    setManagedMatchData(null)
    setManagedMatchError(null)
    setManagedMatchNotice(null)
    setManagedMatchActionKey(null)
  }, [isManageRoute, managedBookingIdParam])

  const loadManagedMatchDetail = useCallback(async () => {
    if (!clubCode || !managedMatchId) return
    setManagedMatchLoading(true)
    setManagedMatchError(null)
    try {
      const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/racquet/open-matches/${managedMatchId}`)
      const payload = await res.json().catch(() => null)
      if (!res.ok || !payload?.ok) {
        setManagedMatchData(null)
        setManagedMatchError("portalPresentation.member_unable_to_load_open_match_details_right_now")
        return
      }
      setManagedMatchData(payload?.data || null)
    } catch (err) {
      setManagedMatchData(null)
      setManagedMatchError("portalPresentation.member_unable_to_load_open_match_details_right_now")
    } finally {
      setManagedMatchLoading(false)
    }
  }, [clubCode, managedMatchId, memberPortalApiGet])

  useEffect(() => {
    if (!managedMatchId) return
    loadManagedMatchDetail()
  }, [loadManagedMatchDetail, managedMatchId])

  const handleManageRequestAction = async ({ bookingId, requestId, action }) => {
    if (!bookingId || !requestId || !action) return
    const actionKey = `${bookingId}:${requestId}:${action}`
    setManagedMatchActionKey(actionKey)
    setManagedMatchNotice(null)
    try {
      const res = await memberPortalApiPost(
        `/api/members-portal/${clubCode}/racquet/open-matches/${bookingId}/requests/${requestId}/${action}`,
        {}
      )
      const payload = await res.json().catch(() => null)
      if (!res.ok || !payload?.ok) {
        setManagedMatchNotice({ type: 'error', message: "portalPresentation.member_unable_to_update_join_request" })
        return
      }
      setManagedMatchNotice({ type: 'success', message: action === 'approve' ? "portalPresentation.member_request_approved" : "portalPresentation.member_request_rejected" })
      await loadManagedMatchDetail()
      await loadOpenMatches()
    } catch (err) {
      setManagedMatchNotice({ type: 'error', message: "portalPresentation.member_unable_to_update_join_request" })
    } finally {
      setManagedMatchActionKey(null)
    }
  }

  const managedMatchPlayers = useMemo(
    () => (Array.isArray(managedMatchData?.players) ? managedMatchData.players.filter((player) => String(player?.status || '').toLowerCase() !== 'left') : []),
    [managedMatchData]
  )
  const managedMatchPendingRequests = useMemo(
    () =>
      (Array.isArray(managedMatchData?.requests) ? managedMatchData.requests : []).filter(
        (request) => String(request?.request_kind || '').toLowerCase() === 'request' && String(request?.status || '').toLowerCase() === 'pending'
      ),
    [managedMatchData]
  )
  const managedMatchPendingInvites = useMemo(
    () =>
      (Array.isArray(managedMatchData?.requests) ? managedMatchData.requests : []).filter(
        (request) => String(request?.request_kind || '').toLowerCase() === 'invite' && String(request?.status || '').toLowerCase() === 'pending'
      ),
    [managedMatchData]
  )

  const bookingRangesByCourtId = useMemo(() => {
    const byCourt = {}
    courts.forEach((court) => {
      const courtId = String(court.id)
      const courtBookings = Array.isArray(bookingsByCourtId?.[courtId]) ? bookingsByCourtId[courtId] : []
      byCourt[courtId] = courtBookings
        .filter((booking) => String(booking?.status || '').toLowerCase() !== 'cancelled')
        .map((booking) => {
          const startMs = new Date(booking?.start_at).getTime()
          const endMs = new Date(booking?.end_at).getTime()
          if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
            return null
          }
          return [startMs, endMs]
        })
        .filter(Boolean)
    })
    return byCourt
  }, [bookingsByCourtId, courts])

  const blockedSlotSet = useMemo(() => {
    const blocked = new Set()
    if (!selectedCourt || !selectedDate || slots.length === 0) {
      return blocked
    }

    const bookingRanges = bookingRangesByCourtId[String(selectedCourt.id)] || []
    if (bookingRanges.length === 0) return blocked

    slots.forEach((slot) => {
      const slotKey = `${slot.startMinutes}-${slot.endMinutes}`
      if (isSlotBlocked(slot, selectedDate, bookingRanges)) {
        blocked.add(slotKey)
      }
    })

    return blocked
  }, [bookingRangesByCourtId, selectedCourt, selectedDate, slots])

  const mobileTimeOptions = useMemo(() => {
    const grouped = new Map()

    courts.forEach((court) => {
      const courtSlots = buildSlots(court, selectedDate)
      const bookingRanges = bookingRangesByCourtId[String(court.id)] || []
      courtSlots.forEach((slot) => {
        if (isSlotBlocked(slot, selectedDate, bookingRanges)) return
        const slotKey = `${slot.startMinutes}-${slot.endMinutes}`
        const existing = grouped.get(slotKey) || {
          slot,
          availableCourts: [],
        }
        existing.availableCourts.push(court)
        grouped.set(slotKey, existing)
      })
    })

    return Array.from(grouped.values()).sort((a, b) => a.slot.startMinutes - b.slot.startMinutes)
  }, [bookingRangesByCourtId, courts, selectedDate])

  const selectedMobileTimeOption = useMemo(
    () => mobileTimeOptions.find((option) => `${option.slot.startMinutes}-${option.slot.endMinutes}` === selectedMobileTimeKey) || null,
    [mobileTimeOptions, selectedMobileTimeKey]
  )

  useEffect(() => {
    setSelectedMobileChoice((choice) => {
      if (!choice || !selectedMobileTimeOption) return null
      const currentCourt = selectedMobileTimeOption.availableCourts.find((court) => String(court.id) === String(choice.court?.id))
      if (!currentCourt || Number(choice.slot?.startMinutes) !== Number(selectedMobileTimeOption.slot.startMinutes)) return null
      const ranges = bookingRangesByCourtId[String(currentCourt.id)] || []
      const dayEnd = parseTimeToMinutes(currentCourt.bookable_end_time)
      const nextBookingStart = ranges.map(([start]) => Math.round((start - startOfDay(selectedDate).getTime()) / 60000)).filter((start) => start > choice.slot.startMinutes).sort((a, b) => a - b)[0]
      const maxAllowedMinutes = Math.max(0, Math.min(dayEnd, nextBookingStart || dayEnd) - choice.slot.startMinutes)
      const validDurations = deriveMemberPortalRacquetDurationOptions({ court: currentCourt, maxAllowedMinutes })
      return validDurations.includes(Number(choice.duration)) ? { ...choice, court: currentCourt } : null
    })
  }, [bookingRangesByCourtId, selectedDate, selectedMobileTimeOption])

  useEffect(() => {
    setSelectedMobileTimeKey((previousKey) => {
      if (!previousKey || !mobileTimeOptions.length) return null
      return mobileTimeOptions.some((option) => `${option.slot.startMinutes}-${option.slot.endMinutes}` === previousKey)
        ? previousKey
        : null
    })
  }, [mobileTimeOptions])

  useEffect(() => {
    const paramDate = parseDateParam(dateParam)
    if (paramDate && !isSameDay(paramDate, selectedDate)) {
      setSelectedDate(paramDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParam])

  useEffect(() => {
    if (!selectedDate) return

    const formatted = formatDateParam(selectedDate)
    if (!formatted) return
    if (dateParam === formatted) return

    const next = new URLSearchParams(searchParams)

    next.set('date', formatted)
    setSearchParams(next, { replace: true })
  }, [dateParam, searchParams, selectedDate, setSearchParams])

  const handleDateSelect = (date) => {
    const normalized = startOfDay(date)
    if (isSameDay(normalized, selectedDate)) return
    setSelectedDate(normalized)
    setSelectedMobileTimeKey(null)
    setSelectedMobileChoice(null)
    setShowAllMobileOpenMatches(false)
  }

  const handleMobileTimeSelect = (slotKey) => {
    setSelectedMobileTimeKey(slotKey)
    setSelectedMobileChoice(null)
    window.requestAnimationFrame(() => {
      mobileCourtSectionRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      })
    })
  }

  const handleSlotClick = (slot, court = selectedCourt, requestedDuration = null) => {
    if (!court || !selectedDate) return

    const startMinutesValue = Number(slot?.startMinutes)
    const startTimeLabel = Number.isFinite(startMinutesValue)
      ? formatMinutesToTime(startMinutesValue)
      : null
    const duration = Number(requestedDuration) || Number(court?.default_duration_minutes) || 60
    const params = new URLSearchParams()
    params.set('courtId', court.id)
    const formattedDate = formatDateParam(selectedDate)
    if (formattedDate) params.set('date', formattedDate)
    if (startTimeLabel) params.set('start', startTimeLabel)
    if (duration) params.set('duration', String(duration))

    navigate(`/${clubCode}/book-racquet/${sportType}/book?${params.toString()}`, {
      state: {
        court,
        date: selectedDate,
        startMinutes: startMinutesValue,
        start: minutesToDate(selectedDate, startMinutesValue),
        duration,
      },
    })
  }

  const handleMobileContinue = () => {
    if (!selectedMobileChoice) return
    handleSlotClick(selectedMobileChoice.slot, selectedMobileChoice.court, selectedMobileChoice.duration)
  }

  const handleRetry = () => setReloadKey((key) => key + 1)

  // The mobile host route owns its complete screen. Returning here is intentional:
  // availability content (especially its fixed transaction dock) is not mounted behind it.
  if (isMobileApp && isManageRoute) {
    const managedStart = managedMatchData?.start_at ? new Date(managedMatchData.start_at) : null
    const managedMeta = managedStart && !Number.isNaN(managedStart.getTime())
      ? managedStart.toLocaleString(locale, { timeZone: clubTimeZone, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).replace(' at ', ' · ')
      : memberText('portalPresentation.member_host_controls')
    return (
      <main className="mp-open-match-manage-screen mp-open-match-manage-screen--mobile">
        <header className="mp-racquet-mobile-header mp-open-match-manage-screen__header">
          <button type="button" onClick={handleCloseManageMatch} aria-label={memberText("portalPresentation.member_back_to_racquet_availability")}><ChevronLeft aria-hidden="true" /></button>
          <div><h1>{memberText("portalPresentation.member_open_match")}</h1><p>{managedMeta}</p></div>
        </header>
        <div className="mp-open-match-manage-screen__content">
          {managedMatchNotice ? <div className={managedMatchNotice.type === 'success' ? 'mp-success' : 'mp-warning'}>{memberText(managedMatchNotice.message)}</div> : null}
          {managedMatchLoading ? (
            <div className="mp-open-match-manage-skeleton" role="status" aria-live="polite">
              <span className="mp-sr-only">{memberText("portalPresentation.member_loading_match_details")}</span>
              <div className="mp-open-match-manage-skeleton__line is-title" /><div className="mp-open-match-manage-skeleton__line" /><div className="mp-open-match-manage-skeleton__players" aria-hidden="true"><i /><i /><i /><i /></div>
            </div>
          ) : managedMatchError ? <div className="mp-warning">{memberText(managedMatchError)}</div> : managedMatchData ? (
            <OpenMatchHostManagementContent managedMatchData={managedMatchData} managedMatchPlayers={managedMatchPlayers} managedMatchPendingRequests={managedMatchPendingRequests} managedMatchPendingInvites={managedMatchPendingInvites} managedMatchActionKey={managedMatchActionKey} onManageRequestAction={handleManageRequestAction} isMobile />
          ) : null}
        </div>
      </main>
    )
  }

  if (!isValidSport) {
    return (
      <div className="mp-panel">
        <div className="mp-panel__header">
          <div>
            <p className="mp-eyebrow">{memberText("portalPresentation.member_racquet")}</p>
            <h4 className="mp-panel__title">{memberText("portalPresentation.member_sport_not_supported")}</h4>
          </div>
        </div>
        <div className="mp-warning">We couldn\'t recognize this sport. Please return to facilities.</div>
      </div>
    )
  }

  return (
    <div className={`mp-panel mp-racquet${isMobileApp ? ' mp-racquet--mobile-discovery' : ''}`}>
      {isMobileApp ? (
        <header className="mp-racquet-mobile-header">
          <button type="button" onClick={() => navigate(`/${clubCode}/book-facilities`)} aria-label={memberText("portalPresentation.member_back_to_facilities")}>
            <ChevronLeft aria-hidden="true" />
          </button>
          <div>
            <h1>{sportLabel}</h1>
            {selectedDate ? <p>{formatCompactDateLabel(selectedDate, locale)}</p> : null}
          </div>
        </header>
      ) : (
        <MemberMobileBookingHeader
          className="mp-racquet__header"
          eyebrow={memberText("portalPresentation.member_racquet")}
          title={memberText("portalPresentation.member_book_sport", { sport: sportLabel })}
          backLabel={memberText("portalPresentation.member_back_to_facilities")}
          backTo={`/${clubCode}/book-facilities`}
          meta={selectedDate ? <p className="mp-text-muted">{formatDateLabel(selectedDate, locale)}</p> : null}
        />
      )}

      {error ? (
        <div className="mp-warning">
          <div>{memberText(error)}</div>
          <button type="button" className="mp-primary" style={{ marginTop: '8px' }} onClick={handleRetry}> {memberText("portalPresentation.member_retry")} </button>
        </div>
      ) : null}
      {openMatchActionNotice ? <div className="mp-success">{memberText(openMatchActionNotice)}</div> : null}

      {loading ? (
        <div className="mp-racquet__loading">{memberText("portalPresentation.member_loading_courts")}</div>
      ) : courts.length === 0 ? (
        <div className="mp-empty">{memberText("portalPresentation.member_no_courts_available_for")} {sportLabel}.</div>
      ) : (
        <>
          <div className={`mp-racquet__mobile-flow${isMobileApp && selectedMobileTimeOption ? ' has-availability-dock' : ''}`}>
            {facilityImage ? (
              <div className="mp-racquet__mobile-hero" aria-hidden>
                <div
                  className="mp-racquet__mobile-hero-image"
                  style={{ backgroundImage: `url(${facilityImage})` }}
                />
              </div>
            ) : null}
            <div className="mp-racquet__date-row">
              <div className="mp-golf-date-scroller mp-racquet__date-scroller" role="tablist" aria-label={memberText("portalPresentation.member_select_court_date")}>
                <div className="mp-golf-day-row mp-racquet__mobile-day-row">
                  {availableDates.map((date) => {
                    const isActive = isSameDay(date, selectedDate)
                    return (
                      <button
                        key={date.toISOString()}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`mp-golf-day ${isActive ? 'active' : ''}`}
                        onClick={() => handleDateSelect(date)}
                      >
                        <span className="mp-golf-day__label">{formatDayTileLabel(date, locale)}</span>
                        <span className="mp-golf-day__date">{formatMonthLabel(date, locale)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {isMobileApp && selectedDate ? (
              <section className="mp-racquet-mobile-open-matches" aria-labelledby="mobile-open-matches-title">
                <div className="mp-racquet-mobile-section-heading">
                  <div>
                    <h2 id="mobile-open-matches-title">{memberText("portalPresentation.member_open_matches")}</h2>
                  </div>
                  {!openMatchesLoading && openMatches.length > 1 ? (
                    <button
                      type="button"
                      className="mp-racquet-mobile-open-matches__see-all"
                      aria-expanded={showAllMobileOpenMatches}
                      onClick={() => setShowAllMobileOpenMatches((visible) => !visible)}
                    >
                      {showAllMobileOpenMatches ? memberText("portalPresentation.member_show_less") : memberText("portalPresentation.member_see_all_count", { count: openMatches.length })} <span aria-hidden="true">›</span>
                    </button>
                  ) : null}
                </div>
                {openMatchesLoading ? (
                  <div className="mp-text-muted">{memberText("portalPresentation.member_loading_open_matches")}</div>
                ) : !openMatches.length ? (
                  <div className="mp-empty subtle">{memberText("portalPresentation.member_no_open_matches_available_for_this_date")}</div>
                ) : (
                  <div className="mp-racquet-discovery-list">
                    {openMatches.slice(0, showAllMobileOpenMatches ? openMatches.length : 1).map((match) => {
                      const confirmed = Number(match?.confirmed_count || 0)
                      const maxPlayers = Number(match?.max_players || 0)
                      const spots = Math.max(0, maxPlayers - confirmed)
                      const participants = Array.isArray(match?.participants) ? match.participants : []
                      return (
                        <button
                          key={match.id}
                          type="button"
                          className="mp-racquet-discovery-card"
                          onClick={() => handleOpenManageMatch(match.id)}
                          aria-label={memberText("portalPresentation.member_open_type_match_at_time_on_court_count_places_left", { type: match.match_type === 'competitive' ? memberText("portalPresentation.member_competitive") : memberText("portalPresentation.member_friendly"), time: new Date(match.start_at).toLocaleTimeString(locale, { timeZone: clubTimeZone, hour: 'numeric', minute: '2-digit' }), court: match.court_name || memberText("racquetPlay.court"), count: spots })}
                        >
                          <div className="mp-racquet-discovery-card__top">
                            <strong>{new Date(match.start_at).toLocaleTimeString(locale, { timeZone: clubTimeZone, hour: 'numeric', minute: '2-digit' })}</strong>
                            <span>{match.court_name || memberText("racquetPlay.court")}</span>
                            <ChevronLeft aria-hidden="true" />
                          </div>
                          <div className="mp-racquet-discovery-card__body">
                            <div className="mp-racquet-discovery-avatars" aria-hidden="true">
                              {participants.slice(0, 3).map((player, index) => player.avatar_url ? (
                                <img key={player.player_id || index} src={player.avatar_url} alt="" />
                              ) : (
                                <span key={player.player_id || index}>{getInitials(player.player_name)}</span>
                              ))}
                            </div>
                            <span>{confirmed}/{maxPlayers} {memberText("portalPresentation.member_players")} {spots} {spots === 1 ? 'place' : 'places'} {memberText("portalPresentation.member_left")}</span>
                          </div>
                          <div className="mp-racquet-discovery-card__meta">
                            <span>{memberText(match.match_type === 'competitive' ? 'portalPresentation.member_competitive' : 'portalPresentation.member_friendly')}</span><PlayingRangeBadge min={match.skill_min} max={match.skill_max} sport={match.sport_type} />
                            <span>{String(match.join_policy || '').toLowerCase() === 'direct_join' ? memberText("portalPresentation.member_direct_join") : memberText("portalPresentation.member_host_approval")}</span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </section>
            ) : null}

            <div className="mp-card">
              <div className="mp-card__header mp-card__header--compact">
                <div>
                  <h5>{memberText("portalPresentation.member_choose_a_time")}</h5>
                </div>
              </div>
              {!mobileTimeOptions.length ? (
                <div className="mp-empty subtle">{memberText("portalPresentation.member_no_available_start_times_for_this_day")}</div>
              ) : (
                <div className="mp-racquet-time-list" role="listbox" aria-label={memberText("portalPresentation.member_available_start_times")}>
                  {mobileTimeOptions.map((option) => {
                    const slotKey = `${option.slot.startMinutes}-${option.slot.endMinutes}`
                    const isActive = slotKey === selectedMobileTimeKey
                    return (
                      <button
                        key={slotKey}
                        type="button"
                        className={`mp-racquet-time-chip ${isActive ? 'is-active' : ''}`}
                        onClick={() => handleMobileTimeSelect(slotKey)}
                      >
                        <span>{formatTime(option.slot.startMinutes, selectedDate, locale)} – {formatTime(option.slot.endMinutes, selectedDate, locale)}</span>
                        <small>{option.availableCourts.length} {memberText("portalPresentation.member_court")}{option.availableCourts.length === 1 ? '' : 's'} {memberText("portalPresentation.member_open")}</small>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {selectedMobileTimeOption ? <div className="mp-card" ref={mobileCourtSectionRef} data-mobile-court-section>
              <div className="mp-card__header mp-card__header--compact">
                <div>
                  <h5>{memberText("portalPresentation.member_choose_a_court")}</h5>
                </div>
              </div>
              {selectedMobileTimeOption.availableCourts.length === 0 ? (
                <div className="mp-empty subtle">{memberText("portalPresentation.member_no_courts_are_available_for_this_time")}</div>
              ) : (
                <div className="mp-racquet-court-choices">
                  {selectedMobileTimeOption.availableCourts.map((court) => (
                    <div key={court.id} className="mp-racquet-court-choice">
                      <MobileCourtChoice court={court} currencyCode={currencyCode} slot={selectedMobileTimeOption.slot}
                        selectedDate={selectedDate} bookingRanges={bookingRangesByCourtId[String(court.id)] || []}
                        selectedChoice={selectedMobileChoice} onSelect={setSelectedMobileChoice} />
                    </div>
                  ))}
                </div>
              )}
            </div> : null}
            {isMobileApp && selectedMobileTimeOption ? <div className="mp-racquet-availability-bar" data-testid="racquet-availability-dock"><strong>{selectedMobileChoice ? `${selectedMobileChoice.court.name} · ${selectedMobileChoice.duration} min${selectedMobileChoice.projectedPrice != null ? ` · ${formatCurrencyAmount(selectedMobileChoice.projectedPrice, currencyCode, { locale })}` : ''}` : memberText("portalPresentation.member_choose_a_court")}</strong><button type="button" disabled={!selectedMobileChoice} onClick={handleMobileContinue}>{memberText("portalPresentation.member_continue")}</button></div> : null}
          </div>

          <div className="mp-racquet__desktop-flow">
            <div className="mp-racquet__grid">
              <div className="mp-racquet__list">
                {courts.map((court) => (
                  <CourtCard
                    key={court.id}
                    court={court}
                    currencyCode={currencyCode}
                    isSelected={String(court.id) === String(selectedCourt?.id)}
                    onSelect={(nextCourt) => setSelectedCourtId(nextCourt.id)}
                  />
                ))}
              </div>

              <div className="mp-racquet__availability">
                <div className="mp-racquet__date-row">
                  <div className="mp-golf-date-scroller mp-racquet__date-scroller" role="tablist" aria-label={memberText("portalPresentation.member_select_court_date")}>
                    {availableDates.map((date) => {
                      const isActive = isSameDay(date, selectedDate)
                      return (
                        <button
                          key={date.toISOString()}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`mp-golf-day ${isActive ? 'active' : ''}`}
                          onClick={() => handleDateSelect(date)}
                        >
                          <span className="mp-golf-day__label">{formatDayTileLabel(date, locale)}</span>
                          <span className="mp-golf-day__date">{formatMonthLabel(date, locale)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div className="mp-card">
                  <div className="mp-card__header">
                    <div>
                      <p className="mp-eyebrow">{memberText("portalPresentation.member_availability")}</p>
                      <h5>{selectedCourt?.name || memberText("portalPresentation.member_select_a_court")}</h5>
                    </div>
                    <div className="mp-card__meta">
                      <MapPin size={16} /> {sportLabel}
                    </div>
                  </div>
                  {slots.length === 0 ? (
                    <div className="mp-empty subtle">{memberText("portalPresentation.member_no_slots_available_for_this_day")}</div>
                  ) : (
                    <div className="mp-availability-grid">
                      {slots.map((slot) => {
                        const slotKey = `${slot.startMinutes}-${slot.endMinutes}`
                        const isBlocked = blockedSlotSet.has(slotKey)

                        return (
                          <button
                            type="button"
                            key={slotKey}
                            className={`mp-slot ${isBlocked ? 'is-disabled' : ''}`}
                            onClick={() => {
                              if (!isBlocked) handleSlotClick(slot)
                            }}
                            disabled={isBlocked}
                          >
                            <div className="mp-slot__time">{formatTime(slot.startMinutes, selectedDate, locale)} – {formatTime(slot.endMinutes, selectedDate, locale)}</div>
                            <div className="mp-slot__hint">{isBlocked ? memberText("portalPresentation.member_unavailable") : memberText("portalPresentation.member_tap_to_book")}</div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="mp-card mp-racquet__desktop-open-matches" style={{ marginTop: 16 }}>
        <div className="mp-card__header mp-card__header--compact">
          <div>
            <p className="mp-eyebrow">{memberText("portalPresentation.member_open_matches_to_join")}</p>
            <h5>{selectedDate ? formatDateLabel(selectedDate, locale) : memberText("portalPresentation.member_selected_day")}</h5>
          </div>
        </div>
        {openMatchesLoading ? (
          <div className="mp-text-muted">{memberText("portalPresentation.member_loading_open_matches")}</div>
        ) : !openMatches.length ? (
          <div className="mp-empty subtle">{memberText("portalPresentation.member_no_open_matches_available_for_this_date")}</div>
        ) : (
          <div className="mp-open-match-grid">
            {openMatches.map((match) => {
              const confirmed = Number(match?.confirmed_count || 0)
              const maxPlayers = Number(match?.max_players || 0)
              const spots = Math.max(0, maxPlayers - confirmed)
              const isHostMatch = openMatchViewers?.[String(match?.id || '')]?.isHost === true
              const viewerPlayerId = match?.viewer_player_id || null
              const viewerPlayerStatus = String(match?.viewer_player_status || '').toLowerCase()
              const viewerAmountDue = Number(match?.viewer_amount_due || 0)
              const viewerRequestStatus = String(match?.viewer_request_status || '').toLowerCase()
              const hasViewerPendingRequest = viewerRequestStatus === 'pending' && !viewerPlayerId
              const isViewerPaymentRequired = ['pending_payment', 'pending_confirmation'].includes(viewerPlayerStatus)
              const isViewerSettled = ['confirmed', 'charged_to_account'].includes(viewerPlayerStatus)
              const canJoin = !isHostMatch && spots > 0 && match.lifecycle === 'open'
              const participants = Array.isArray(match?.participants) ? match.participants : []
              const viewerParticipant = participants.find((player) => String(player?.player_id || '') === String(viewerPlayerId || ''))
              const visibleSlots = Math.max(1, maxPlayers || 4)
              return (
                <article key={match.id} className="mp-open-match-card">
                  <div className="mp-open-match-card__header">
                    <strong>{new Date(match.start_at).toLocaleTimeString(locale, { timeZone: clubTimeZone, hour: 'numeric', minute: '2-digit' })}</strong>
                    <span className="mp-chip subtle">{match.court_name || memberText("racquetPlay.court")}</span>
                  </div>
                  <div className="mp-open-match-card__summary">
                    <span>{confirmed}/{maxPlayers} {memberText("portalPresentation.member_players_2")}</span>
                    <span>{spots} {memberText("portalPresentation.member_spots_left")}</span>
                  </div>
                  <div className="mp-open-match-card__chips">
                    <span className="mp-chip subtle">{memberText(match.match_type === 'competitive' ? 'portalPresentation.member_competitive' : 'portalPresentation.member_friendly')}</span>
                    <span className="mp-chip subtle">{String(match.join_policy || '').toLowerCase() === 'direct_join' ? memberText("portalPresentation.member_direct_join") : memberText("portalPresentation.member_host_approval")}</span>
                    <PlayingRangeBadge min={match.skill_min} max={match.skill_max} sport={match.sport_type} />
                  </div>
                  <div className="mp-open-match-card__slots">
                    {Array.from({ length: visibleSlots }).map((_, slotIndex) => {
                      const player = participants[slotIndex] || null
                      const isHostPlayer = player && String(player.member_id || '') === String(match.host_member_id || '')
                      return (
                        <div key={`${match.id}-slot-${slotIndex}`} className={`mp-open-match-slot ${player ? '' : 'is-empty'}`}>
                          {player ? (
                            <>
                              {player.avatar_url ? (
                                <img src={player.avatar_url} alt={player.player_name || memberText("portalPresentation.member_player_caption")} className="mp-open-match-slot__avatar" />
                              ) : (
                                <div className="mp-open-match-slot__avatar mp-open-match-slot__avatar--fallback">{getInitials(player.player_name)}</div>
                              )}
                              <div className="mp-open-match-slot__meta">
                                <span>{player.player_name || memberText("portalPresentation.member_player_caption")}</span>
                                <small>{mapOpenMatchParticipantPublicLabel({ isHost: isHostPlayer }, memberText)}</small><PlayingLevel value={player.playing_level} sport={match.sport_type} />
                              </div>
                            </>
                          ) : (
                            <div className="mp-open-match-slot__empty">{memberText("portalPresentation.member_vacant_spot")}</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="mp-open-match-card__actions">
                    {isHostMatch ? (
                      <>
                        <div className="mp-chip">{memberText("portalPresentation.member_your_match")}</div>
                        <button type="button" className="mp-btn mp-btn--ghost" onClick={() => handleOpenManageMatch(match.id)}> {memberText("portalPresentation.member_view_requests")} </button>
                      </>
                    ) : viewerPlayerId ? (
                      <>
                        <div className="mp-chip subtle">{mapParticipantStatusLabel(viewerPlayerStatus, memberText)}</div>
                        {isViewerPaymentRequired ? (
                          <div className="mp-open-match-card__payment-grid">
                            <div className="mp-open-match-card__payment-indicator">{memberText("portalPresentation.member_payment_required")}</div>
                            <div className="mp-open-match-card__payment-indicator">{formatCurrencyAmount(viewerAmountDue, currencyCode, { locale })} {memberText("portalPresentation.member_due")}</div>
                            <button
                              type="button"
                              className="mp-btn mp-open-match-card__payment-button mp-open-match-card__payment-button--account"
                              onClick={() => handleOpenMatchPayment({ bookingId: match.id, playerId: viewerPlayerId, method: 'charge_account' })}
                            > {memberText("portalPresentation.member_add_to_account")} </button>
                            <button
                              type="button"
                              className="mp-btn mp-btn--primary mp-open-match-card__payment-button"
                              onClick={() => handleOpenPayOnlineModal({
                                matchId: match.id,
                                playerId: viewerPlayerId,
                                amountDue: viewerAmountDue,
                                courtName: match.court_name,
                                startAt: match.start_at,
                                viewerName: viewerParticipant?.player_name,
                              })}
                            > {memberText("portalPresentation.member_pay_online")} </button>
                          </div>
                        ) : null}
                        {isViewerSettled ? <div className="mp-chip subtle">{memberText("portalPresentation.member_you_are_in_this_match")}</div> : null}
                      </>
                    ) : hasViewerPendingRequest ? (
                      <div className="mp-chip subtle">{memberText("portalPresentation.member_awaiting_approval")}</div>
                    ) : (
                      <button type="button" className="mp-btn mp-btn--primary" onClick={() => handleJoinOpenMatch(match.id)} disabled={!canJoin}>
                        {spots <= 0 || match.lifecycle !== 'open'
                          ? memberText("portalPresentation.member_full")
                          : String(match.join_policy || '').toLowerCase() === 'direct_join'
                            ? memberText("portalPresentation.member_join_now")
                            : memberText("portalPresentation.member_request_spot")}
                      </button>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
      {isManageRoute ? (
        <div className="mp-open-match-manage-screen">
          <div className="mp-open-match-manage-screen__header">
            <button type="button" className="mp-btn mp-btn--ghost" onClick={handleCloseManageMatch}>{memberText("portalPresentation.member_back")}</button>
            <div>
              <p className="mp-eyebrow">{memberText("portalPresentation.member_host_controls")}</p>
              <h5>{memberText("portalPresentation.member_manage_open_match")}</h5>
            </div>
          </div>
          <div className="mp-open-match-manage-screen__content">
            {managedMatchNotice ? (
              <div className={managedMatchNotice.type === 'success' ? 'mp-success' : 'mp-warning'}>{memberText(managedMatchNotice.message)}</div>
            ) : null}
            {managedMatchLoading ? (
              <div className="mp-text-muted">{memberText("portalPresentation.member_loading_match_details")}</div>
            ) : managedMatchError ? (
              <div className="mp-warning">{memberText(managedMatchError)}</div>
            ) : managedMatchData ? (
              <OpenMatchHostManagementContent
                managedMatchData={managedMatchData}
                managedMatchPlayers={managedMatchPlayers}
                managedMatchPendingRequests={managedMatchPendingRequests}
                managedMatchPendingInvites={managedMatchPendingInvites}
                managedMatchActionKey={managedMatchActionKey}
                onManageRequestAction={handleManageRequestAction}
                isMobile
              />
            ) : null}
          </div>
        </div>
      ) : managedMatchId ? (
        <div className="mp-modal" role="dialog" aria-modal="true" aria-label={memberText("portalPresentation.member_manage_open_match_requests")}>
          <div className="mp-modal__backdrop" onClick={handleCloseManageMatch} />
          <div className="mp-modal__panel mp-open-match-manage-modal">
            <div className="mp-modal__header">
              <div>
                <p className="mp-eyebrow">{memberText("portalPresentation.member_host_controls")}</p>
                <h5>{memberText("portalPresentation.member_manage_open_match")}</h5>
              </div>
              <button type="button" className="mp-modal__close" onClick={handleCloseManageMatch} aria-label={memberText("portalPresentation.member_close")}>×</button>
            </div>
            <div className="mp-modal__content">
              {managedMatchNotice ? (
                <div className={managedMatchNotice.type === 'success' ? 'mp-success' : 'mp-warning'}>{memberText(managedMatchNotice.message)}</div>
              ) : null}
              {managedMatchLoading ? (
                <div className="mp-text-muted">{memberText("portalPresentation.member_loading_match_details")}</div>
              ) : managedMatchError ? (
                <div className="mp-warning">{memberText(managedMatchError)}</div>
              ) : managedMatchData ? (
                <OpenMatchHostManagementContent
                  managedMatchData={managedMatchData}
                  managedMatchPlayers={managedMatchPlayers}
                  managedMatchPendingRequests={managedMatchPendingRequests}
                  managedMatchPendingInvites={managedMatchPendingInvites}
                  managedMatchActionKey={managedMatchActionKey}
                  onManageRequestAction={handleManageRequestAction}
                  isMobile={false}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {payOnlineModalMatch ? (
        <div className="mp-modal" role="dialog" aria-modal="true" aria-label={memberText("portalPresentation.member_pay_online_for_open_match")}>
          <div className="mp-modal__backdrop" onClick={() => setPayOnlineModalMatch(null)} />
          <div className="mp-modal__panel mp-open-match-payment-modal">
            <div className="mp-modal__header">
              <div>
                <p className="mp-eyebrow">{memberText("portalPresentation.member_checkout")}</p>
                <h5>{memberText("portalPresentation.member_pay_online")}</h5>
              </div>
              <button type="button" className="mp-modal__close" onClick={() => setPayOnlineModalMatch(null)} aria-label={memberText("portalPresentation.member_close")}>×</button>
            </div>
            <div className="mp-modal__content mp-open-match-payment-modal__content">
              <section className="mp-open-match-payment-modal__summary">
                <h6>{memberText("portalPresentation.member_payment_summary")}</h6>
                <div className="mp-open-match-payment-modal__summary-grid">
                  <div>
                    <small>{memberText("portalPresentation.member_match_time_date")}</small>
                    <strong>{formatMatchDateTime(payOnlineModalMatch.startAt, locale, memberText, clubTimeZone)}</strong>
                  </div>
                  <div>
                    <small>{memberText("racquetPlay.court")}</small>
                    <strong>{payOnlineModalMatch.courtName || memberText('portalPresentation.member_court')}</strong>
                  </div>
                  <div>
                    <small>{memberText("portalPresentation.member_member")}</small>
                    <strong>{payOnlineModalMatch.viewerName || memberText('portalPresentation.member_member')}</strong>
                  </div>
                  <div>
                    <small>{memberText("portalPresentation.amount_due")}</small>
                    <strong>{formatCurrencyAmount(payOnlineModalMatch.amountDue, currencyCode, { locale })}</strong>
                  </div>
                </div>
              </section>
              <section className="mp-open-match-payment-modal__form">
                <div className="mp-field">
                  <label className="mp-field__label" htmlFor="mp-pay-cardholder-name">{memberText("portalPresentation.member_cardholder_name")}</label>
                  <input id="mp-pay-cardholder-name" type="text" placeholder="Alex Morgan" autoComplete="cc-name" />
                </div>
                <div className="mp-field">
                  <label className="mp-field__label" htmlFor="mp-pay-card-number">{memberText("portalPresentation.member_card_number")}</label>
                  <input id="mp-pay-card-number" type="text" inputMode="numeric" placeholder="4242 4242 4242 4242" autoComplete="cc-number" />
                </div>
                <div className="mp-open-match-payment-modal__row">
                  <div className="mp-field">
                    <label className="mp-field__label" htmlFor="mp-pay-card-expiry">{memberText("portalPresentation.member_expiry")}</label>
                    <input id="mp-pay-card-expiry" type="text" inputMode="numeric" placeholder={memberText("portalPresentation.member_mm_yy")} autoComplete="cc-exp" />
                  </div>
                  <div className="mp-field">
                    <label className="mp-field__label" htmlFor="mp-pay-card-cvc">CVC</label>
                    <input id="mp-pay-card-cvc" type="text" inputMode="numeric" placeholder="123" autoComplete="cc-csc" />
                  </div>
                  <div className="mp-field">
                    <label className="mp-field__label" htmlFor="mp-pay-card-zip">{memberText("portalPresentation.member_billing_postcode_zip")}</label>
                    <input id="mp-pay-card-zip" type="text" placeholder="SW1A 1AA" autoComplete="postal-code" />
                  </div>
                </div>
              </section>
              <p className="mp-open-match-payment-modal__helper">{memberText("portalPresentation.member_online_card_payments_are_not_enabled_yet")}</p>
            </div>
            <div className="mp-modal__actions mp-open-match-payment-modal__actions">
              <button type="button" className="mp-btn mp-btn--ghost" onClick={() => setPayOnlineModalMatch(null)}> {memberText("portalPresentation.member_cancel")} </button>
              <button type="button" className="mp-btn mp-btn--primary" disabled> {memberText("portalPresentation.member_confirm_payment")} </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
